import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { App, Button, Modal, Result, Spin } from 'antd'
import type { CustomerIssueInput } from '@super-mega/server/contracts/bill'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { CustomerIssuePreviewContent } from './customer-issue-preview.tsx'
import { getPreviousMonthCreatedTime } from './search.ts'

export function CustomerIssueButton({ customerId }: { customerId: number }) {
  const trpc = useTRPC(),
    cache = useQueryClient(),
    router = useRouter(),
    { message } = App.useApp()
  const [open, setOpen] = useState(false)
  const [canRetryIssue, setCanRetryIssue] = useState(false)
  const [range, setRange] = useState(getPreviousMonthCreatedTime)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })
  const [selectedAccounts, setSelectedAccounts] = useState<Record<number, number | undefined>>({})
  const pendingRequest = useRef<CustomerIssueInput | undefined>(undefined)
  const submitting = useRef(false)
  const issue = useMutation(trpc.bill.issueCustomer.mutationOptions())
  const preview = useQuery({
    ...trpc.bill.previewCustomerIssue.queryOptions({ customerId, ...range, ...pagination }),
    enabled: open && !issue.isPending && !issue.isSuccess && !issue.isError,
    refetchOnWindowFocus: false
  })
  const accounts = Object.fromEntries(
    (preview.data?.organizations ?? []).map((org) => {
      const selected = selectedAccounts[org.id]
      if (org.accounts.some((account) => account.id === selected)) return [org.id, selected]
      if (org.accounts.length === 1) return [org.id, org.accounts[0].id]
      return [org.id, undefined]
    })
  )
  const canConfirm =
    !preview.isFetching &&
    !preview.isError &&
    !!preview.data?.billCount &&
    preview.data.organizations.every((org) => !!accounts[org.id])
  let noticeHref: string | undefined
  if (issue.isSuccess) {
    noticeHref = router.buildLocation({ to: '/finance/receipts', search: { ids: issue.data.groupIds } }).href
  }

  const submit = async (input: CustomerIssueInput) => {
    if (submitting.current) return
    submitting.current = true
    pendingRequest.current = input
    try {
      await issue.mutateAsync(input)
    } catch (error) {
      // 业务冲突重新预览；网络结果不确定时保留同一幂等请求以安全重试。
      if (
        error instanceof Error &&
        'data' in error &&
        (error.data as { code?: string } | undefined)?.code === 'CONFLICT'
      ) {
        pendingRequest.current = undefined
      }
      setCanRetryIssue(!!pendingRequest.current)
      submitting.current = false
      return
    }
    pendingRequest.current = undefined
    submitting.current = false
    await refreshMoneyQueries(cache, trpc).catch(() => {
      void message.warning('出账成功，列表刷新失败，请手动刷新')
    })
  }

  const start = () => {
    if (issue.isPending) return
    if (!pendingRequest.current) {
      setRange(getPreviousMonthCreatedTime())
      setPagination({ current: 1, pageSize: 20 })
      setSelectedAccounts({})
      issue.reset()
    }
    setOpen(true)
  }

  const confirm = () => {
    if (!canConfirm || !preview.data || issue.isPending) return
    void submit({
      customerId,
      ...range,
      expectedState: preview.data.expectedState,
      idempotencyKey: crypto.randomUUID(),
      accounts: preview.data.organizations.map((org) => ({ organizationId: org.id, accountId: accounts[org.id]! }))
    })
  }

  const retry = () => {
    if (pendingRequest.current) {
      void submit(pendingRequest.current)
      return
    }
    // 业务冲突必须重新预览，并由用户再次确认新的候选集。
    issue.reset()
  }

  const renderContent = () => {
    if (issue.isPending) {
      return (
        <div role="status" aria-live="polite" aria-busy="true">
          <Result
            icon={<Spin size="large" />}
            title="正在出账，请稍候"
            subTitle="正在处理账单并生成收款通知单。订单较多时可能需要等待几分钟，请保持页面打开。"
          />
        </div>
      )
    }
    if (issue.isSuccess) {
      const result = issue.data
      return (
        <div role="status" aria-live="polite">
          <Result
            status="success"
            title="出账完成"
            subTitle={
              <div className="space-y-2">
                <p>
                  已出账 {result.billCount} 张，生成 {result.groupIds.length} 份收款通知单。
                </p>
                {result.pendingCount + result.zeroCount > 0 && (
                  <p>
                    跳过待核金额 {result.pendingCount} 张、零金额 {result.zeroCount} 张。
                  </p>
                )}
              </div>
            }
            extra={
              <Button type="primary" href={noticeHref} target="_blank" rel="noopener noreferrer">
                打开收款通知单
              </Button>
            }
          />
        </div>
      )
    }
    if (issue.isError) {
      let retryText = '重新预览'
      if (canRetryIssue) retryText = '重试出账'
      return (
        <Result
          status="error"
          title="出账未完成"
          subTitle={getErrorMessage(issue.error, '出账失败，请重试')}
          extra={
            <Button type="primary" onClick={retry}>
              {retryText}
            </Button>
          }
        />
      )
    }
    return (
      <CustomerIssuePreviewContent
        data={preview.data}
        range={range}
        loading={preview.isFetching}
        error={preview.isError ? getErrorMessage(preview.error, '读取账单失败，请重新预览') : undefined}
        accounts={accounts}
        pagination={pagination}
        onRangeChange={(next) => {
          setRange(next)
          setPagination((previous) => ({ ...previous, current: 1 }))
          setSelectedAccounts({})
        }}
        onPageChange={(current, pageSize) => setPagination({ current, pageSize })}
        onAccountChange={(organizationId, accountId) =>
          setSelectedAccounts((previous) => ({ ...previous, [organizationId]: accountId }))
        }
        onRefresh={() => void preview.refetch()}
      />
    )
  }

  let footer = null
  if (!issue.isPending) {
    footer = <Button onClick={() => setOpen(false)}>关闭</Button>
    if (!issue.isSuccess && !issue.isError) {
      footer = (
        <>
          <Button onClick={() => setOpen(false)}>取消</Button>
          <Button type="primary" disabled={!canConfirm} onClick={confirm}>
            确认出账
          </Button>
        </>
      )
    }
  }

  return (
    <>
      <Button type="primary" loading={issue.isPending} onClick={start}>
        一键出账
      </Button>
      {noticeHref && !open && (
        <Button type="link" href={noticeHref} target="_blank" rel="noopener noreferrer">
          打开收款通知单
        </Button>
      )}
      <Modal
        open={open}
        title="客户一键出账 · 预览"
        width="72rem"
        centered
        closable={!issue.isPending}
        keyboard={!issue.isPending}
        mask={{ closable: false }}
        footer={footer}
        onCancel={() => setOpen(false)}
      >
        {renderContent()}
      </Modal>
    </>
  )
}
